import { useCallback, useEffect, useSyncExternalStore } from 'react';
import api from '../services/api';

/*
 * Ajustes por cuenta (GET/PATCH /api/account/settings). Esquema y valores por defecto
 * viven en el servidor (server/services/accountSettings.js): aqui no se duplican.
 *
 * Un solo almacen por pestaña del navegador, compartido por todos los componentes que
 * llaman al hook, asi que la pagina de ajustes y cualquier otra pantalla ven lo mismo.
 *
 * Actualizacion optimista: lo que se ve es lo confirmado por el servidor con los parches
 * aun en vuelo aplicados encima. Si un PATCH falla se quita solo ese parche, de modo que
 * el interruptor vuelve a su valor sin tocar los cambios posteriores que si funcionaron.
 * Los PATCH salen en orden (uno detras de otro).
 */

const STALE_MS = 30 * 1000;

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

function deepMerge(base, patch) {
  const out = { ...base };
  for (const [k, v] of Object.entries(patch)) {
    out[k] = isObject(v) && isObject(base?.[k]) ? deepMerge(base[k], v) : v;
  }
  return out;
}

const store = {
  rfidId: null,
  confirmed: null,
  loadedAt: 0,
  pending: [],
  loading: false,
  error: null,
  loadPromise: null,
  queue: Promise.resolve(),
  listeners: new Set(),
  snapshot: { settings: null, loading: false, error: null },
};

function publish() {
  const settings = store.confirmed ? store.pending.reduce(deepMerge, store.confirmed) : null;
  const next = { settings, loading: store.loading, error: store.error };
  const prev = store.snapshot;
  if (prev.settings === next.settings && prev.loading === next.loading && prev.error === next.error) return;
  store.snapshot = next;
  store.listeners.forEach((l) => l());
}

// Otra cuenta en el mismo navegador: nada de lo anterior vale.
function ensureAccount() {
  const rfidId = localStorage.getItem('rfidId');
  if (store.rfidId === rfidId) return;
  store.rfidId = rfidId;
  store.confirmed = null;
  store.loadedAt = 0;
  store.pending = [];
  store.loading = false;
  store.error = null;
  store.loadPromise = null;
  store.queue = Promise.resolve();
  publish();
}

function load() {
  ensureAccount();
  if (!store.rfidId) return Promise.resolve();
  if (store.loadPromise) return store.loadPromise;
  const owner = store.rfidId;
  store.loading = store.confirmed === null; // con datos en pantalla, el refresco es silencioso
  store.error = null;
  publish();
  store.loadPromise = api.get('/account/settings')
    .then((res) => {
      if (store.rfidId !== owner) return;
      store.confirmed = res.data;
      store.loadedAt = Date.now();
    })
    .catch((err) => {
      if (store.rfidId !== owner) return;
      // Si ya habia datos se conservan; solo se marca el fallo si no hay nada que mostrar.
      if (store.confirmed === null) store.error = err;
    })
    .finally(() => {
      if (store.rfidId === owner) {
        store.loading = false;
        store.loadPromise = null;
        publish();
      }
    });
  return store.loadPromise;
}

function update(partial) {
  ensureAccount();
  if (!store.confirmed) return Promise.reject(new Error('settings_not_loaded'));
  const owner = store.rfidId;
  const patch = JSON.parse(JSON.stringify(partial));
  store.pending.push(patch);
  publish();

  const run = async () => {
    try {
      const res = await api.patch('/account/settings', patch);
      if (store.rfidId === owner) {
        store.confirmed = res.data;
        store.loadedAt = Date.now();
      }
    } finally {
      if (store.rfidId === owner) {
        store.pending = store.pending.filter((p) => p !== patch);
        publish();
      }
    }
  };
  const result = store.queue.then(run);
  store.queue = result.catch(() => {});
  return result;
}

function subscribe(listener) {
  store.listeners.add(listener);
  return () => store.listeners.delete(listener);
}

const EMPTY = { settings: null, loading: false, error: null };

// Si cambio la cuenta del navegador y aun no se ha reiniciado el almacen, no se enseña lo de la anterior.
const getSnapshot = () => (store.rfidId === localStorage.getItem('rfidId') ? store.snapshot : EMPTY);

/** Solo para pruebas: deja el almacen como recien cargado el modulo. */
export function __resetAccountSettingsStore() {
  store.rfidId = null;
  store.confirmed = null;
  store.pending = [];
  store.loading = false;
  store.error = null;
  store.loadPromise = null;
  store.queue = Promise.resolve();
  store.loadedAt = 0;
  store.snapshot = { settings: null, loading: false, error: null };
}

/**
 * Los ajustes de la cuenta fuera de React (servicios): los que haya, cargandolos si hace falta. Un refresco
 * silencioso si llevan mas de 30 s. Resuelve null si no hay sesion o la carga falla.
 */
export async function ensureAccountSettings() {
  ensureAccount();
  if (store.confirmed === null) await load();
  else if (Date.now() - store.loadedAt > STALE_MS) load();
  return store.confirmed ? store.pending.reduce(deepMerge, store.confirmed) : null;
}

/**
 * const { settings, loading, error, reload, update } = useAccountSettings();
 *  - settings: objeto completo (o null mientras carga o si fallo la primera carga).
 *  - update({ privacy: { siteIcons: false } }): optimista; devuelve una promesa que
 *    rechaza con el error de axios si el servidor no lo acepta (el valor ya se revirtio).
 */
function useAccountSettings() {
  const snap = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    ensureAccount();
    if (store.confirmed === null || Date.now() - store.loadedAt > STALE_MS) load();
  }, []);

  const reload = useCallback(() => load(), []);
  const updateSettings = useCallback((partial) => update(partial), []);

  return { settings: snap.settings, loading: snap.loading, error: snap.error, reload, update: updateSettings };
}

export default useAccountSettings;
